import {
  trackBaseTemplateHeroCommunityCtaGA,
  trackBaseTemplateHeroMejoresAmigosCtaGA,
  trackBaseTemplateUIGA,
} from "@/lib/analytics";
import {
  buildHeroCtaSessionContext,
  heroClickKindFromElementId,
  markHeroCtaClicked,
  postHeroCtaSlackFunnel,
} from "@/lib/heroCtaFunnel";
import {
  trackBaseTemplateUIClickPostHog,
  type BaseTemplateAnalyticsName,
  type BaseTemplateClickSurface,
} from "@/lib/posthogAnalytics";

export type { BaseTemplateAnalyticsName, BaseTemplateClickSurface };

const HERO_PAGE_MOUNT_MS =
  typeof window !== "undefined" ? performance.timeOrigin || Date.now() : Date.now();

export function trackBaseTemplateClick(params: {
  templateName: BaseTemplateAnalyticsName;
  elementId: string;
  label: string;
  surface: BaseTemplateClickSurface;
  href?: string;
  countryCode?: string;
  extra?: Record<string, string | number | boolean | undefined>;
}) {
  const heroGeoCtx =
    params.surface === "hero"
      ? buildHeroCtaSessionContext({
          ctaResolved:
            typeof params.extra?.cta_resolved === "string"
              ? params.extra.cta_resolved
              : undefined,
          ctaGeoSource:
            typeof params.extra?.cta_geo_source === "string"
              ? params.extra.cta_geo_source
              : undefined,
          heroCtaType:
            typeof params.extra?.hero_cta_type === "string"
              ? params.extra.hero_cta_type
              : undefined,
          href: params.href,
          isArgentina: params.countryCode === "AR",
        })
      : {};

  const mergedExtra = {
    ...heroGeoCtx,
    ...params.extra,
  };

  if (params.surface === "hero") {
    markHeroCtaClicked();
    const slackKind = heroClickKindFromElementId(params.elementId);
    if (slackKind) {
      const secondsToClick = Math.max(
        0,
        Math.round((Date.now() - HERO_PAGE_MOUNT_MS) / 1000),
      );
      postHeroCtaSlackFunnel(slackKind, {
        templateName: params.templateName,
        country: params.countryCode,
        elementId: params.elementId,
        label: params.label,
        href: params.href,
        timeOnPageSeconds: secondsToClick,
        lastStep: "hero_cta_click",
        ctaResolved: String(mergedExtra.cta_resolved ?? ""),
        ctaGeoSource: String(mergedExtra.cta_geo_source ?? ""),
        replayNearNow: true,
        extra: {
          seconds_to_click: secondsToClick,
          ...(mergedExtra.payment_provider
            ? { payment_provider: String(mergedExtra.payment_provider) }
            : {}),
          ...(mergedExtra.cta_tier ? { cta_tier: String(mergedExtra.cta_tier) } : {}),
          ...(mergedExtra.mp_plan_id ? { mp_plan_id: String(mergedExtra.mp_plan_id) } : {}),
          ...heroGeoCtx,
        },
      });
    }
  }

  trackBaseTemplateUIClickPostHog({
    templateName: params.templateName,
    elementId: params.elementId,
    label: params.label,
    surface: params.surface,
    href: params.href,
    countryCode: params.countryCode,
    extra: mergedExtra,
  });
  if (
    params.surface === "hero" &&
    params.elementId === "hero_cta_community"
  ) {
    trackBaseTemplateHeroCommunityCtaGA({
      templateName: params.templateName,
      label: params.label,
      href: params.href,
      extra: mergedExtra as Record<string, unknown> | undefined,
    });
  }
  if (
    params.surface === "hero" &&
    params.elementId === "hero_cta_mejores_amigos"
  ) {
    trackBaseTemplateHeroMejoresAmigosCtaGA({
      templateName: params.templateName,
      label: params.label,
      href: params.href,
      extra: mergedExtra as Record<string, unknown> | undefined,
    });
  }
  // Nota (2026-06-20): el dispatch birthday-específico (elementId=hero_cta_birthday
  // o /r/cumple destination) fue removido porque cta_type=birthday se remapeó
  // visualmente a Mejores Amigos. Ahora hero_cta_birthday no se emite — siempre
  // sale hero_cta_mejores_amigos. birthdayCtaAnalytics + birthdayCtaAttribution
  // siguen disponibles para otros surfaces (no-hero) que los necesiten.
  trackBaseTemplateUIGA({
    templateName: params.templateName,
    elementId: params.elementId,
    label: params.label,
    surface: params.surface,
    href: params.href,
    extra: mergedExtra as Record<string, unknown> | undefined,
  });
}
